import { useState, useEffect, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { TopProgressBar } from '@/components/ui/TopProgressBar';
import { SessionTimeoutGuard } from '@/components/auth/SessionTimeoutGuard';
import { useGlobalRealtime } from '@/hooks/useGlobalRealtime';

const SIDEBAR_COLLAPSED_KEY = 'mediflow-care-sidebar-collapsed';

export function AppShell() {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try {
            return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
        } catch {
            return false;
        }
    });

    useGlobalRealtime();

    // Keyboard shortcut: Cmd/Ctrl + B to toggle sidebar
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
                e.preventDefault();
                toggleSidebar();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const toggleSidebar = () => {
        setSidebarCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
            } catch { /* ignore */ }
            return next;
        });
    };

    return (
        <MotionConfig reducedMotion="user">
            <SessionTimeoutGuard>
            <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--surface-canvas)' }}>
                <TopProgressBar />

                <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

                <div className="flex flex-1 flex-col overflow-hidden min-w-0">
                    <Topbar onMenuToggle={toggleSidebar} />
                    <main className="flex-1 overflow-y-auto">
                        <div className="h-full p-6 max-w-[1600px] mx-auto">
                            <Suspense fallback={
                                <div className="flex h-full items-center justify-center">
                                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 dark:border-neutral-700 border-t-brand-500" />
                                </div>
                            }>
                                <Outlet />
                            </Suspense>
                        </div>
                    </main>
                </div>
            </div>
            </SessionTimeoutGuard>
        </MotionConfig>
    );
}
